import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  addConsultationTemplateField,
  createConsultationFieldDefinition,
  createConsultationTemplate,
  getConsultationTemplateById,
  listConsultationFieldDefinitions,
  listConsultationTemplates,
  updateConsultationFieldDefinition,
  updateConsultationTemplate,
  updateConsultationTemplateField,
} from '../services/consultationService';
import type { ConsultationFieldDefinition, ConsultationTemplate } from '../types/Consultation';
import {
  buildAuthoringBundleFileName,
  buildFieldDefinitionsBundle,
  buildTemplatesBundle,
  downloadConsultationAuthoringBundle,
  parseConsultationAuthoringBundle,
  type ConsultationAuthoringImportScope,
} from '../utils/consultationAuthoringBundle';
import {
  formatConsultationAuthoringImportSummary,
  importConsultationAuthoringBundle,
} from '../utils/consultationAuthoringImport';
import styles from './ConsultationExportMenu.module.css';

export type ConsultationAuthoringBundleMenuProps = {
  scope: ConsultationAuthoringImportScope;
  fieldDefinitions: ConsultationFieldDefinition[];
  templates?: ConsultationTemplate[];
  disabled?: boolean;
  onCompleted?: (message: string) => void;
  onError?: (message: string) => void;
  onImported?: () => void;
};

export const ConsultationAuthoringBundleMenu: React.FC<ConsultationAuthoringBundleMenuProps> = ({
  scope,
  fieldDefinitions,
  templates = [],
  disabled = false,
  onCompleted,
  onError,
  onImported,
}) => {
  const { t } = useTranslation(['consultations', 'common']);
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const handleExport = async () => {
    setOpen(false);
    setBusy(true);

    try {
      if (scope === 'fieldDefinitions') {
        const bundle = buildFieldDefinitionsBundle(fieldDefinitions);
        downloadConsultationAuthoringBundle(bundle, buildAuthoringBundleFileName('field-definitions'));
      } else {
        const [detailedTemplates, allFieldDefinitions] = await Promise.all([
          Promise.all(
            templates.map(async (template) => {
              const detailed = await getConsultationTemplateById(template.id);
              return detailed ?? template;
            }),
          ),
          listConsultationFieldDefinitions(),
        ]);
        const bundle = buildTemplatesBundle(detailedTemplates, allFieldDefinitions);
        downloadConsultationAuthoringBundle(bundle, buildAuthoringBundleFileName('templates'));
      }

      onCompleted?.(scope === 'fieldDefinitions'
        ? t('consultationFieldDefinitionsExported', { defaultValue: 'Field definitions exported to JSON.' })
        : t('consultationTemplatesExported', { defaultValue: 'Templates exported to JSON.' }));
    } catch (exportError) {
      onError?.(exportError instanceof Error
        ? exportError.message
        : t('consultationAuthoringExportFailed', { defaultValue: 'Failed to export consultation authoring data.' }));
    } finally {
      setBusy(false);
    }
  };

  const handleImportClick = () => {
    setOpen(false);
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setBusy(true);

    try {
      const text = await file.text();
      const bundle = parseConsultationAuthoringBundle(JSON.parse(text));
      if (scope === 'fieldDefinitions' && bundle.fieldDefinitions.length === 0) {
        throw new Error('Import file does not contain field definitions.');
      }
      if (scope === 'templates' && bundle.templates.length === 0) {
        throw new Error('Import file does not contain templates.');
      }
      const summary = await importConsultationAuthoringBundle(bundle, scope, {
        listFieldDefinitions: () => listConsultationFieldDefinitions(),
        listTemplates: () => listConsultationTemplates(),
        createFieldDefinition: (input) => createConsultationFieldDefinition(input),
        updateFieldDefinition: (id, input) => updateConsultationFieldDefinition(id, input),
        createTemplate: (input) => createConsultationTemplate(input),
        updateTemplate: (id, input) => updateConsultationTemplate(id, input),
        getTemplateById: (id) => getConsultationTemplateById(id),
        addTemplateField: (templateId, input) => addConsultationTemplateField(templateId, input),
        updateTemplateField: (templateId, fieldDefinitionId, input) => updateConsultationTemplateField(templateId, fieldDefinitionId, input),
      });

      onImported?.();
      onCompleted?.(formatConsultationAuthoringImportSummary(summary, scope));
    } catch (importError) {
      onError?.(importError instanceof Error
        ? importError.message
        : t('consultationAuthoringImportFailed', { defaultValue: 'Failed to import consultation authoring data.' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.menu} ref={menuRef}>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={(event) => void handleImportFile(event)}
      />
      <button
        type="button"
        className="btn btn-secondary"
        disabled={disabled || busy}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {busy
          ? t('working', { ns: 'common', defaultValue: 'Working…' })
          : `${t('importExport', { defaultValue: 'Import / Export' })} ▾`}
      </button>
      {open ? (
        <div className={styles.menuList} role="menu">
          <button
            type="button"
            className={styles.menuItem}
            role="menuitem"
            onClick={() => void handleExport()}
          >
            {t('exportToJson', { defaultValue: 'Export to JSON' })}
          </button>
          <button
            type="button"
            className={styles.menuItem}
            role="menuitem"
            onClick={handleImportClick}
          >
            {t('importFromJson', { defaultValue: 'Import from JSON' })}
          </button>
        </div>
      ) : null}
    </div>
  );
};
